import { CartItem } from '../slices/cart';
import { ProductJoinInfoDto } from '../types/generated';

/**
 * Approximate cart total for API validation calls (voucher validation, etc).
 * Uses client-side product data which may not reflect salepoint overrides exactly.
 * Display source of truth is useCartPriceGetter hook (uses server-resolved prices).
 */
export const calculateCartTotal = (
  cartItems: CartItem[],
  products: ProductJoinInfoDto[] | undefined
): number => {
  if (!products) return 0;

  return cartItems.reduce((sum, cartItem) => {
    const { partials, variantIds, productId, quantity } = cartItem;
    const product = products.find((p) => p.id === productId);
    if (!product) return sum;

    // Use variant price if available, otherwise base product price
    const variantPrice = product.variants?.find((pv) => {
      const isSelected = variantIds?.includes(pv.id);
      const isAnotherSelected = variantIds?.some((vid) =>
        product.variants.find((v) => v.id === vid)?.groupId === pv.groupId && vid !== pv.id
      );
      return pv.price && (isSelected || (pv.isDefault && !isAnotherSelected));
    })?.price;

    const basePrice = variantPrice || product.price || 0;

    // Add partials price (component calculation)
    const partialsPrice =
      partials?.reduce((pSum, partial) => {
        const componentProduct = products.find((p) => p.id === partial.partialProductId);
        return pSum + (componentProduct?.price || 0) * partial.quantity;
      }, 0) || 0;

    const itemTotal = (basePrice + partialsPrice) * quantity;
    return sum + itemTotal;
  }, 0);
};
